import React from 'react'
import { Navigate } from 'react-router-dom'
import { useGetAuthenticatedUser } from '../../../Flux/Selector/index.js'
import { isGlobalAdmin } from '../../../Model/User/IUser.js'
import { Card } from '../AdminStatsPage/StatsUi.js'
import { SpinChrome } from './SpinChrome.js'
import { useSelectedRun } from './useSelectedRun.js'

/** Per-source reach denominators (backend `spin.metrics.reach-denominators` in application.yml). */
const REACH_DENOMINATORS: Array<[string, number, string]> = [
  ['BOLD', 6, 'never fills domain or kingdom'],
  ['NCBI', 7, 'kingdom ~never filled'],
  ['Silva', 7, 'kingdom ~never filled'],
  ['Consensus', 7, 'kingdom ~never filled'],
  ['RDP', 8, 'fills the whole ladder'],
  ['(other)', 8, 'default fallback']
]

/**
 * Static reference page: what "unity" and "reach" mean, aligned to the backend SQL
 * (SpinCurrentSetService.UNITY_SQL / SpinDetailService.DETAIL_SCORES_SQL). Linked from the metrics nav.
 */
export const SpinDefinitionsPage = () => {
  const user = useGetAuthenticatedUser()
  const sel = useSelectedRun()

  if (!user) return <></>
  if (!isGlobalAdmin(user)) return <Navigate to="/home" replace />

  return (
    <div className="space-y-8">
      <SpinChrome title="SPIN · Definitions" sel={sel} />

      <p className="text-sm text-gray-500">
        How the SPIN cohesion metrics are computed. These mirror the backend queries exactly — if the SQL changes, this page should too.
      </p>

      <Card title="Unity — taxonomic agreement">
        <div className="space-y-3 text-sm">
          <p>
            <b>Unity</b> asks whether a SPIN&rsquo;s members agree on their taxonomy. It is computed <b>per taxonomy source</b> (BOLD /
            NCBI / RDP / Consensus), over the SPIN&rsquo;s <b>current members</b>.
          </p>
          <p>
            For each of the 8 ranks (domain → species) we take the <b>modal share</b> = (members carrying the single most common value at
            that rank) ÷ (members with data for that source), then <b>average the modal share across all 8 ranks</b>.
          </p>
          <ul className="list-disc list-inside text-gray-600 space-y-1">
            <li>
              <b>1.0</b> = every member agrees on the whole lineage. <b>Lower</b> = the cluster mixes taxa.
            </li>
            <li>
              <b>Empty handling:</b> values that are <code>null</code>, <code>&apos;&apos;</code> or <code>&apos;nan&apos;</code> (any
              casing, trimmed) collapse to a single &ldquo;empty&rdquo; bucket. So an all-unresolved rank scores 1.0, and a
              null/&lsquo;&rsquo;/nan mix is not read as disagreement — but a partially-resolved rank still counts the unresolved share
              against agreement.
            </li>
            <li>
              <b>Singleton caveat:</b> a SPIN with one member trivially scores 1.0, so the top unity bin dominates whole-set views.
            </li>
          </ul>
        </div>
      </Card>

      <Card title="Reach — taxonomic depth">
        <div className="space-y-3 text-sm">
          <p>
            <b>Reach</b> asks how <b>deeply resolved</b> a SPIN&rsquo;s members are (again per source). For each member we count its{' '}
            <b>filled</b> (non-empty, same normalisation as unity) ranks and divide by a <b>per-source denominator</b> — the number of
            ranks that source usually fills when resolved to species — then <b>average over the members</b>. Higher ⇒ members resolved
            deeper. Unity and reach pair up: <b>unity = agreement, reach = depth</b>.
          </p>
          <div className="overflow-x-auto">
            <table className="table table-xs w-auto">
              <thead>
                <tr>
                  <th>Source</th>
                  <th className="text-right">Denominator</th>
                  <th>Why</th>
                </tr>
              </thead>
              <tbody>
                {REACH_DENOMINATORS.map(([src, denom, why]) => (
                  <tr key={src}>
                    <td>{src}</td>
                    <td className="text-right tabular-nums">{denom}</td>
                    <td className="text-gray-600">{why}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-gray-500 text-xs">
            Denominators are configurable (<code>spin.metrics.reach-denominators</code> in application.yml) and derived from prod
            <code> otu_taxonomy_result</code> (hit_rank 0): the ranks a source fills for the species-resolved population.
          </p>
        </div>
      </Card>

      <Card title="The “current SPIN set as of …”">
        <p className="text-sm text-gray-600">
          All cohesion metrics are computed over the <b>current SPIN set as of a point in time</b>, not a run. Each OTU&rsquo;s current
          SPIN is its <b>latest</b> <code>spin_to_otu</code> assignment with <code>assigned_at ≤</code> the selected timestamp
          (latest-wins per OTU) — currency is by timestamp, never by run id, so a single current set can mix SPINs first minted in
          different runs.
        </p>
      </Card>
    </div>
  )
}
